import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { UserModel } from '../../models/user.model';
import { UtilsService } from '../../shared/utils.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { NgForm } from '@angular/forms';
import { AppService } from '../../app.service';
import { StorageService } from '../../shared/storage.service';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-login-credentials',
  templateUrl: './login-credentials.component.html',
  styleUrls: ['./login-credentials.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LoginCredentialsComponent {
  // Used to show the current user's email and username.
  email: string = '';
  username: string = '';

  // The email and username as last loaded from, or saved to, the API. Used to tell whether the user is
  // changing a credential, which is when the API demands the current password.
  originalEmail: string = '';
  originalUsername: string = '';

  // The user's CURRENT password. The API verifies it whenever the email, username or password changes.
  currentPassword: string = '';
  currentPasswordFieldType: string = 'password';

  // Server side message for the current password field after a 422. Cleared as soon as the user edits it.
  currentPasswordError: string = '';

  // Used to update the user's password.
  password: string = '';
  passwordConfirmation: string = '';

  // Used to show/hide the password fields.
  passwordFieldType: string = 'password';
  confirmPasswordFieldType: string = 'password';

  // Used to show password strength.
  passwordValidated: boolean = false;

  inProgress: boolean = false;

  constructor(
    public utils: UtilsService,
    private api: ApiRequestService,
    private app: AppService,
    private storage: StorageService,
    private rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // Check core user profile permission before loading any data.
    if (
      !this.rbac.userCanAccess([
        RBACService.RBAC_F.CORE_PERMISSION_USER_PROFILE
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access this feature.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // Make an API request to get the current authenticated user's profile.
    this.api
      .makeRequest('get', 'v2/user')
      .then((response: UserModel) => {
        // Extract the email and username from the response.
        this.email = response.email;
        this.username = response.username;
        this.originalEmail = response.email;
        this.originalUsername = response.username;
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Whether this save changes the email address, username or password, which is when the API requires the
   * current password. This only decides whether the field is mandatory in the form: the API re-evaluates
   * the same rule server side and stays the authority (email compared case-insensitively after trimming,
   * username compared exactly, password counts when non-empty).
   */
  get currentPasswordRequired(): boolean {
    return (
      (this.email || '').trim().toLowerCase() !== (this.originalEmail || '').trim().toLowerCase() ||
      this.username !== this.originalUsername ||
      this.password.length > 0
    );
  }

  /**
   * Clears the server side message once the user starts correcting the current password.
   */
  onCurrentPasswordChange(): void {
    this.currentPasswordError = '';
  }

  /**
   * Update the authenticated user's login credentials.
   * @param form The form object.
   */
  onUpdateLoginCredentials(form: NgForm) {
    // Check if all required fields are filled out.
    if (!form.valid) {
      this.utils.showModal(
        'Error Message',
        'Please make sure you enter all required fields.'
      );
      return;
    }
    // Check if another request is in progress.
    if (this.inProgress) {
      return;
    }
    // The API refuses a credential change without the current password, so ask for it before the
    // confirmation prompt instead of after a round trip.
    if (this.currentPasswordRequired && !this.currentPassword) {
      this.currentPasswordError = this.utils.getLangTerm(
        'users.form.current_password_required',
        'Your current password is required to change your email address, username or password.'
      );
      return;
    }
    // Construct a confirmation message.
    let confirmationMessage =
      'Are you sure you want to update your login credentials?';
    if (this.password) {
      confirmationMessage +=
        ' You changed your password and will be logged out of all other devices.';
    }
    // Show the confirmation message.
    this.utils.showModal('Confirmation Needed', confirmationMessage, () => {
      // Set the in progress state to true.
      this.inProgress = true;
      // Make an API request to update the user's login credentials.
      this.api
        .makeRequest('put', 'v2/user/login-credentials', {
          email: this.email,
          username: this.username,
          password: this.password,
          password_confirmation: this.passwordConfirmation,
          current_password: this.currentPassword
        })
        .then((response) => {
          this.utils.showToast('Your login credentials has been updated.');
          // Re-authenticate the user.
          this.handleSignInResponse(response);
          // Never keep any password in the form once the save went through. The email and username that
          // were just saved become the new baseline for the "is the current password required" rule.
          this.currentPassword = '';
          this.currentPasswordError = '';
          this.password = '';
          this.passwordConfirmation = '';
          this.originalEmail = this.email;
          this.originalUsername = this.username;
          this.inProgress = false;
        })
        .catch((errorResponse) => {
          this.handleUpdateError(errorResponse);
          this.inProgress = false;
        });
    });
  }

  /**
   * Reports a failed credential update. Every typed value is deliberately left in the form so the user can
   * correct one field and retry.
   *
   * - 422 with `errors.current_password` (missing or wrong): shown on the current password field. The
   *   error interceptor has already opened its generic error dialog for the 422, and calling
   *   handleAPIErrors() as well would only overwrite that shared dialog with a second copy of the text.
   * - 403 with `personal_token_forbidden`: the session holds a Personal API token, which the API refuses
   *   on credential routes. Not an RBAC denial, so tell the user to sign in with their account credentials.
   * - Anything else (other validation failures, server errors): the standard handler.
   */
  private handleUpdateError(errorResponse: any): void {
    const currentPasswordErrors: string[] = errorResponse?.error?.errors?.current_password;
    if (errorResponse?.status === 422 && Array.isArray(currentPasswordErrors) && currentPasswordErrors.length) {
      this.currentPasswordError = currentPasswordErrors[0];
      return;
    }

    if (errorResponse?.status === 403 && errorResponse?.error?.personal_token_forbidden) {
      this.utils.showModal(
        'Error Message',
        errorResponse.error.message ||
          'Personal API tokens cannot be used for this action. Sign in with your account credentials instead.'
      );
      return;
    }

    this.utils.handleAPIErrors(errorResponse);
  }

  /**
   * Send a password reset request to the user.
   */
  onResetPassword() {
    // Get confirmation before sending the password reset request.
    this.utils.showModal(
      'Confirm Password Reset',
      'Are you sure you want to request a password reset? An email will be sent to you with a unique link to update your password.',
      () => {
        // Send a request to reset the password.
        this.api
          .makeRequest('post', 'v2/password-reset-request', {
            email: this.app.user.email
          })
          .then((response) => {
            this.utils.showToast(response.message);
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  /**
   * Store the user's auth, account and profile details.
   * @param response
   * @private
   */
  private handleSignInResponse(response) {
    // Check if all required properties are present.
    if (
      !response.account ||
      !response.user ||
      !response.user_account_link ||
      !response.api_token
    ) {
      this.utils.showToast(
        'We could not refresh your account details. You may need to log in again.'
      );
      return;
    }
    // Set the app auth status to true.
    this.app.isAuthenticated = true;
    // Store the API token.
    this.storage.set('AuthToken', response.api_token);
    // Apply the user profile, account details and user link details for permissions.
    this.app.user.apply(response.user);
    this.app.account.apply(response.account);
    this.app.userAccountLink.apply(response.user_account_link);
  }

  getOutputPasswordValidated($event) {
    this.passwordValidated = $event;
  }

  /**
   * Toggles the visibility of the password field based on the given field parameter.
   *
   * @param {string} field - The name of the field to toggle visibility for ('currentPassword', 'password' or 'confirmPassword').
   * @returns {void}
   */
  togglePasswordVisibility(field: string): void {
    if (field === 'password') {
      this.passwordFieldType =
        this.passwordFieldType === 'password' ? 'text' : 'password';
    } else if (field === 'currentPassword') {
      this.currentPasswordFieldType =
        this.currentPasswordFieldType === 'password' ? 'text' : 'password';
    } else if (field === 'confirmPassword') {
      this.confirmPasswordFieldType =
        this.confirmPasswordFieldType === 'password' ? 'text' : 'password';
    }
  }
}
